import { FilterPopoverTabs } from '@/Enums/Pixels/FilterPopoverTabs.enum';
import { IControlOption } from '@/Interfaces/ControlOption.interface';
import AddFilterButton from '@/components/Filter/AddFilterButton';
import { AdvertisersTabButton, CampaignsTabButton } from '@/components/Filter/TabButtons';
import TabFilterPopover from '@/components/Filter/TabFilterPopover';
import TabPanel from '@/components/TabPanel';
import TabSelect from '@/components/TabSelect';
import { BASE_TAB_ID } from '@/constants/changeLogHistory';
import { areArraysOfObjectsEqual } from '@/helpers/comparators';
import { getFilterOptions } from '@/helpers/filters/filterOptions';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import {
  selectChangeLogHistoryCampaignOptions,
  selectChangeLogHistoryFilters,
} from '@/store/selectors/changeLogHistory.selectors';
import { selectMeRole } from '@/store/selectors/me.selectors';
import {
  selectRibeyeAllClients,
  selectRibeyeAllWhitelabels,
  selectSharedApiLoading,
} from '@/store/selectors/sharedApi.selectors';
import { setFilters } from '@/store/slices/changeLogHistory.slice';
import { a11yProps } from '@/utils/tabProps';
import { Tab, Tabs, tabClasses } from '@mui/material';
import { MouseEventHandler, SyntheticEvent, useCallback, useMemo, useState } from 'react';

const FILTER_TABS = [FilterPopoverTabs.Advertisers, FilterPopoverTabs.Campaigns];

const FilterPopover = () => {
  //#region state
  const dispatch = useAppDispatch();
  const clients = useAppSelector(selectRibeyeAllClients);
  const whitelabels = useAppSelector(selectRibeyeAllWhitelabels);
  const loadingFilterSource = useAppSelector(selectSharedApiLoading);
  const filters = useAppSelector(selectChangeLogHistoryFilters);
  const { options: campaignOptions, loading: loadingCampaignOptions } = useAppSelector(
    selectChangeLogHistoryCampaignOptions,
  );
  const meRole = useAppSelector(selectMeRole);

  const [filterAnchorEl, setFilterAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [currentTab, setCurrentTab] = useState(FilterPopoverTabs.Advertisers);

  const [pendingAdvertisers, setPendingAdvertisers] = useState<IControlOption[]>(filters.advertisers);
  const [pendingCampaigns, setPendingCampaigns] = useState<IControlOption[]>(filters.campaigns);
  //#endregion

  //#region useMemo
  const filtersOpen = useMemo(() => Boolean(filterAnchorEl), [filterAnchorEl]);
  const filtersId = useMemo(() => (filtersOpen ? 'filters-popover' : undefined), [filtersOpen]);

  const options = useMemo(
    () =>
      getFilterOptions({
        userRole: meRole!,
        clients,
        whitelabels,
        options: { prefetchAdvertisers: true },
      }),
    [clients, meRole, whitelabels],
  );

  const commonTabPanelProps = useMemo(
    () => ({
      className: 'w-full',
      baseTabId: BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );
  //#endregion

  //#region useCallback
  const resetPendingValues = useCallback(() => {
    setPendingAdvertisers(filters.advertisers);
    setPendingCampaigns(filters.campaigns);
  }, [filters.advertisers, filters.campaigns]);

  const handleTabChange = useCallback((_: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  }, []);

  const handleOpen = useCallback<MouseEventHandler<HTMLButtonElement>>(
    ({ currentTarget }) => {
      resetPendingValues();
      setFilterAnchorEl(currentTarget);
    },
    [resetPendingValues],
  );

  const handleClose = useCallback(
    (_: SyntheticEvent, reason: string) => {
      if (reason) return;
      setFilterAnchorEl(null);
      resetPendingValues();
      setCurrentTab(FILTER_TABS[0]);
    },
    [resetPendingValues],
  );

  const handleSubmit = useCallback(() => {
    if (
      areArraysOfObjectsEqual(filters.advertisers, pendingAdvertisers) &&
      areArraysOfObjectsEqual(filters.campaigns, pendingCampaigns)
    ) {
      // @ts-expect-error mui mismatch
      handleClose();
      return;
    }
    dispatch(
      setFilters({
        advertisers: pendingAdvertisers,
        campaigns: pendingCampaigns,
      }),
    );
    // @ts-expect-error mui mismatch
    handleClose();
  }, [filters.advertisers, filters.campaigns, handleClose, pendingAdvertisers, pendingCampaigns, dispatch]);

  const updateAdvertisersValue = useCallback((updatedAdvertisers: IControlOption[] = []) => {
    setPendingAdvertisers(updatedAdvertisers);
    setPendingCampaigns([]);
  }, []);

  const updateCampaignsValue = useCallback((updatedCampaigns: IControlOption[] = []) => {
    setPendingCampaigns(updatedCampaigns);
  }, []);
  //#endregion

  return (
    <>
      <AddFilterButton
        onClick={handleOpen}
        describedBy={filtersId}
        variant='outlined'
        sx={{ minWidth: 150, height: 'auto' }}
      />
      <TabFilterPopover
        id={filtersId}
        open={filtersOpen}
        anchorEl={filterAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Tabs
          value={currentTab}
          orientation='vertical'
          onChange={handleTabChange}
          aria-label='changelog filter tabs'
          sx={{
            minHeight: 'unset',
            background: '#1B43320A',
            padding: '16px',
            minWidth: '230px',
            [`& .${tabClasses.root}`]: {
              paddingX: 0,
              minHeight: 'unset',
              justifyContent: 'flex-start',
              paddingLeft: '16px',
              paddingRight: '16px',
            },
            [`& .${tabClasses.selected}`]: {
              background: '#1B43321F',
            },
          }}
          slotProps={{
            indicator: {
              hidden: true,
            },
          }}
        >
          <Tab
            value={FilterPopoverTabs.Advertisers}
            component={AdvertisersTabButton}
            {...a11yProps(FilterPopoverTabs.Advertisers, BASE_TAB_ID)}
          />
          <Tab
            value={FilterPopoverTabs.Campaigns}
            component={CampaignsTabButton}
            {...a11yProps(FilterPopoverTabs.Campaigns, BASE_TAB_ID)}
          />
        </Tabs>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Advertisers}>
          <TabSelect
            label='Advertiser'
            loading={loadingFilterSource}
            options={options.advertiser}
            value={pendingAdvertisers}
            onChange={updateAdvertisersValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
        <TabPanel {...commonTabPanelProps} index={FilterPopoverTabs.Campaigns}>
          <TabSelect
            label='Campaign'
            loading={loadingCampaignOptions}
            options={campaignOptions}
            value={pendingCampaigns}
            onChange={updateCampaignsValue}
            onClose={handleClose}
            onSubmit={handleSubmit}
          />
        </TabPanel>
      </TabFilterPopover>
    </>
  );
};

export default FilterPopover;
